import type { SurveyConfig, SurveyState } from './types'

export const getSurveyState = async (surveyId: string): Promise<SurveyState> => {
  const stored = localStorage.getItem(`csat-survey-${surveyId}`)

  return stored
    ? JSON.parse(stored)
    : {
        dismissed: false,
        submitted: false,
        lastShownDate: new Date().toISOString(),
      }
}

export const setSurveyState = async (surveyId: string, state: Partial<SurveyState>): Promise<void> => {
  const currentState = await getSurveyState(surveyId)
  localStorage.setItem(
    `csat-survey-${surveyId}`,
    JSON.stringify({
      ...currentState,
      ...state,
    })
  )
}

export const shouldShowSurvey = async (surveyConfig: SurveyConfig): Promise<boolean> => {
  const state = await getSurveyState(surveyConfig.id)

  if (state.submitted) {
    return false
  }

  if (state.dismissed) {
    const lastShown = new Date(state.lastShownDate)
    const daysSinceLastShown = (new Date().getTime() - lastShown.getTime()) / (1000 * 3600 * 24)
    if (daysSinceLastShown < surveyConfig.cooldownDays) {
      return false
    }
  }

  if (surveyConfig.endsAt && new Date() > new Date(surveyConfig.endsAt)) {
    return false
  }

  // Check if survey is scheduled for later
  if (!isSurveyScheduledForNow(surveyConfig.id)) {
    return false
  }

  return true
}

// Visit counter utilities
export const getVisitCount = (): number => {
  const stored = localStorage.getItem('dashboard-visit-count')
  return stored ? parseInt(stored, 10) : 0
}

export const incrementVisitCount = (): number => {
  const currentCount = getVisitCount()
  const newCount = currentCount + 1
  localStorage.setItem('dashboard-visit-count', newCount.toString())
  return newCount
}

export const hasReachedVisitCountThreshold = (threshold: number): boolean => {
  return getVisitCount() >= threshold
}

// First login time utilities
export const getFirstLoginTime = (): string | null => {
  return localStorage.getItem('dashboard-first-login')
}

export const setFirstLoginTime = (): void => {
  const existingTime = getFirstLoginTime()
  if (!existingTime) {
    localStorage.setItem('dashboard-first-login', new Date().toISOString())
  }
}

export const hasPassedTimeThreshold = (days: number): boolean => {
  const firstLoginTime = getFirstLoginTime()
  if (!firstLoginTime) {
    return false
  }
  const firstLogin = new Date(firstLoginTime)
  const daysSinceFirstLogin = (new Date().getTime() - firstLogin.getTime()) / (1000 * 3600 * 24)
  return daysSinceFirstLogin >= days
}

// Survey scheduling utilities
export const getSurveySchedule = (surveyId: string): string | null => {
  return localStorage.getItem(`csat-survey-schedule-${surveyId}`)
}

export const setSurveySchedule = (surveyId: string, scheduledTime: string): void => {
  localStorage.setItem(`csat-survey-schedule-${surveyId}`, scheduledTime)
}

export const removeSurveySchedule = (surveyId: string): void => {
  localStorage.removeItem(`csat-survey-schedule-${surveyId}`)
}

export const isSurveyScheduledForNow = (surveyId: string): boolean => {
  const scheduledTime = getSurveySchedule(surveyId)
  if (!scheduledTime) {
    return true // No schedule means it can be shown now
  }

  const scheduled = new Date(scheduledTime)
  const now = new Date()
  return now >= scheduled
}

export const postponeSurvey = (surveyId: string, hoursToPostpone: number = 24): void => {
  const postponedTime = new Date()
  postponedTime.setHours(postponedTime.getHours() + hoursToPostpone)
  setSurveySchedule(surveyId, postponedTime.toISOString())
}

export const getActiveSurveyFromStorage = (): string | null => {
  return localStorage.getItem('csat-active-survey')
}

export const setActiveSurveyInStorage = (surveyId: string): void => {
  localStorage.setItem('csat-active-survey', surveyId)
}

export const clearActiveSurveyFromStorage = (): void => {
  localStorage.removeItem('csat-active-survey')
}
